import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, 
  Code, 
  Shield, 
  Rocket, 
  Users, 
  Zap, 
  Clock,
  CheckCircle,
  Database,
  Settings,
  Wrench,
  GitBranch,
  BarChart,
  BookOpen,
  Network,
  HeartHandshake,
  Cpu
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Définition des styles d'animation
const styles = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }

  .floating {
    animation: float 6s ease-in-out infinite;
  }

  .pulsing {
    animation: pulse 2s ease-in-out infinite;
  }
`;

// Composant d'arrière-plan animé
const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10">
    {/* Gradient radial */}
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
    
    {/* Bordure supérieure brillante */}
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />

    {/* Grille d'arrière-plan */}
    <div className="absolute inset-0 opacity-30">
      <div className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
          maskImage: 'radial-gradient(circle at 50% 50%, black, transparent 80%)'
        }}
      />
    </div>

    {/* Particules flottantes */}
    <div className="absolute inset-0 overflow-hidden">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-[#017D7E]/20"
          style={{
            width: Math.random() * 4 + 'px',
            height: Math.random() * 4 + 'px',
            top: Math.random() * 100 + '%',
            left: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 6 + 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 4}s`
          }}
        />
      ))}
    </div>
  </div>
);

// Composants utilitaires définis avant le composant principal
const StatBox = ({ number, text }) => (
  <div className="group bg-white/5 rounded-lg p-4 border border-white/10 w-full md:w-auto
                hover:border-green-500/50 transition-all duration-300">
    <div className="text-xl md:text-2xl font-bold text-[#017D7E] mb-1 group-hover:scale-110 
                   transition-transform duration-300">
      {number}
    </div>
    <div className="text-sm text-white/70">{text}</div>
  </div>
);

const ComponentCard = ({ icon: Icon, title, description, features }) => (
  <div className="group bg-white/5 rounded-xl p-4 md:p-6 border border-white/10 
                hover:border-green-500/50 transition-all duration-300 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent
                  -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
    <div className="relative z-10">
      <div className="p-3 rounded-lg bg-[#017D7E]/10 w-fit mb-4 transform group-hover:scale-110 
                    transition-transform duration-300">
        <Icon className="w-6 h-6 text-[#017D7E]" />
      </div>
      <h3 className="text-lg font-bold mb-2 group-hover:text-[#017D7E] 
                   transition-colors duration-300">
        {title}
      </h3>
      <p className="text-white/70 text-sm mb-4">{description}</p>
      <ul className="space-y-1">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-2 text-white/70 text-sm 
                                 group-hover:text-white transition-colors duration-200">
            <div className="w-1 h-1 rounded-full bg-green-400 transform group-hover:scale-150 
                        transition-transform duration-300" />
            {feature}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const BenefitCard = ({ icon: Icon, title, description, features }) => (
  <div className="group bg-white/5 rounded-xl p-6 md:p-8 border border-white/10 
                hover:border-green-500/50 transition-all duration-300 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent 
                  -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
    <div className="relative z-10">
      <div className="p-3 rounded-lg bg-[#017D7E]/10 w-fit mb-6 transform group-hover:scale-110 
                    transition-transform duration-300">
        <Icon className="w-6 h-6 text-[#017D7E]" />
      </div>
      <h3 className="text-xl font-bold mb-4 group-hover:text-[#017D7E] 
                   transition-colors duration-300">
        {title}
      </h3>
      <p className="text-white/70 mb-6">{description}</p>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start md:items-center gap-2 text-white/70 
                                 group-hover:text-white transition-colors duration-200">
            <CheckCircle className="w-4 h-4 mt-1 md:mt-0 text-[#017D7E] flex-shrink-0 
                                transform group-hover:scale-110 transition-transform duration-300" />
            {feature}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const ExpertiseCard = ({ icon: Icon, title, description }) => (
  <div className="group text-center">
    <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-[#017D7E]/10 flex items-center justify-center 
                  mx-auto mb-4 md:mb-6 transform group-hover:scale-110 transition-transform duration-300">
      <Icon className="w-7 h-7 md:w-8 md:h-8 text-[#017D7E]" />
    </div>
    <h3 className="text-lg md:text-xl font-bold mb-2 group-hover:text-[#017D7E] 
                 transition-colors duration-300">
      {title}
    </h3>
    <p className="text-white/70 text-sm md:text-base">{description}</p>
  </div>
);

const ProcessStep = ({ number, title, description, features }) => (
  <div className="group bg-white/5 rounded-xl p-6 md:p-8 border border-white/10 
                hover:border-green-500/50 transition-all duration-300">
    <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
      <div className="text-2xl md:text-3xl font-bold text-[#017D7E] transform group-hover:scale-110 
                    transition-transform duration-300">
        {number}
      </div>
      <div>
        <h3 className="text-xl font-bold mb-2 group-hover:text-[#017D7E] 
                     transition-colors duration-300">
          {title}
        </h3>
        <p className="text-white/70 mb-4">{description}</p>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-white/70 group/item">
              <CheckCircle className="w-4 h-4 mt-1 text-[#017D7E] flex-shrink-0 
                                  group-hover/item:scale-110 transition-transform duration-200" />
              <span className="text-sm md:text-base group-hover/item:text-white 
                           transition-colors duration-200">
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const ServiceItem = ({ icon: Icon, title, description }) => (
  <div className="group flex items-start gap-4">
    <div className="p-2 rounded-lg bg-[#017D7E]/10 flex-shrink-0 transform group-hover:scale-110 
                  transition-transform duration-300">
      <Icon className="w-5 h-5 text-[#017D7E]" />
    </div>
    <div>
      <h3 className="text-base md:text-lg font-semibold mb-2 group-hover:text-[#017D7E] 
                   transition-colors duration-300">
        {title}
      </h3>
      <p className="text-white/70 text-sm md:text-base">{description}</p>
    </div>
  </div>
);

// Composant principal Factory
const Factory = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState({});

  // Injection des styles CSS
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  // Animation au scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach(
      el => observer.observe(el)
    );

    return () => observer.disconnect();
  }, []);

  // Effet de parallaxe
  useEffect(() => {
    const handleMouseMove = (e) => {
      const parallaxElements = document.querySelectorAll('.parallax');
      parallaxElements.forEach(element => {
        const speed = element.getAttribute('data-speed') || 0.2;
        const x = (window.innerWidth - e.pageX * speed) / 100;
        const y = (window.innerHeight - e.pageY * speed) / 100;
        element.style.transform = `translateX(${x}px) translateY(${y}px)`;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen text-white">
      <AnimatedBackground />

      {/* Hero Section */}
      <div className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 
                        to-[#017D7E]/20 text-[#017D7E] mb-8 group hover:scale-105 transition-all"
                 data-animate
                 id="hero-badge">
              <div className="relative z-10">Service Développement Factory</div>
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-[#017D7E]/10 
                          rounded-full animate-pulse" />
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 md:mb-8 leading-tight"
                data-animate
                id="hero-title"
                style={{
                  opacity: isVisible['hero-title'] ? 1 : 0,
                  transform: isVisible['hero-title'] ? 'translateY(0)' : 'translateY(2rem)',
                  transition: 'all 1000ms'
                }}>
              Développez
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                Vos Solutions Sur Mesure
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/70 mb-8 md:mb-12 parallax"
               data-speed="0.1">
              Notre Factory développe des solutions financières sur mesure en combinant composants certifiés,
              expertise métier et méthodologie industrielle pour garantir qualité, conformité et time-to-market optimal.
            </p>

            <div className="flex flex-col md:flex-row gap-4 justify-center mb-8"
                 data-animate
                 id="hero-stats"
                 style={{
                   opacity: isVisible['hero-stats'] ? 1 : 0,
                   transform: isVisible['hero-stats'] ? 'translateY(0)' : 'translateY(2rem)',
                   transition: 'all 1000ms 200ms'
                 }}>
              <StatBox number="60%" text="Réduction time-to-market" />
              <StatBox number="40%" text="Réduction des coûts" />
              <StatBox number="100%" text="Conformité réglementaire" />
            </div>

            <button
              onClick={() => navigate('/contact')}
              className="group px-6 md:px-8 py-3 md:py-4 rounded-lg bg-gradient-to-r from-green-500 
                      to-[#017D7E] hover:from-green-600 hover:to-emerald-600 text-white font-medium 
                      transition-all flex items-center gap-2 mx-auto relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                Discuter de votre projet
                <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 
                          to-transparent -translate-x-full group-hover:translate-x-full 
                          transition-transform duration-1000" />
            </button>
          </div>
        </div>
      </div>

      {/* Components Section */}
      <section className="py-16 md:py-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6"
                  data-animate
                  id="components-title"
                  style={{
                    opacity: isVisible['components-title'] ? 1 : 0,
                    transform: isVisible['components-title'] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: 'all 1000ms'
                  }}>
                Nos Composants Certifiés
              </h2>
              <p className="text-lg md:text-xl text-white/70">
                Une bibliothèque complète de composants financiers réutilisables
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                {
                  icon: Cpu,
                  title: "Core Banking",
                  description: "Composants essentiels bancaires",
                  features: ["Gestion comptes", "Transactions", "Reporting"]
                },
                {
                  icon: Shield,
                  title: "Risk & Compliance",
                  description: "Modules réglementaires",
                  features: ["KYC/AML", "Scoring", "Reporting"]
                },
                {
                  icon: Database,
                  title: "Data Management",
                  description: "Gestion des données",
                  features: ["ETL", "Data Quality", "Analytics"]
                },
                {
                  icon: Network,
                  title: "API & Connecteurs",
                  description: "Connectivité système",
                  features: ["REST APIs", "Middleware", "ESB"]
                }
              ].map((component, index) => (
                <div
                  key={index}
                  data-animate
                  id={`component-${index}`}
                  style={{
                    opacity: isVisible[`component-${index}`] ? 1 : 0,
                    transform: isVisible[`component-${index}`] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: `all 1000ms ${index * 200}ms`
                  }}
                >
                  <ComponentCard {...component} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-transparent to-black/30 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6"
                  data-animate
                  id="benefits-title"
                  style={{
                    opacity: isVisible['benefits-title'] ? 1 : 0,
                    transform: isVisible['benefits-title'] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: 'all 1000ms'
                  }}>
                Les Avantages de Notre Approche Factory
              </h2>
              <p className="text-lg md:text-xl text-white/70">
                Une méthodologie industrielle éprouvée pour des développements maîtrisés
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: Code,
                  title: "Composants Certifiés",
                  description: "Accélérez vos développements avec notre bibliothèque de composants métier certifiés.",
                  features: [
                    "Réduction des délais de 60%",
                    "Qualité garantie",
                    "Maintenance simplifiée",
                    "Documentation complète"
                  ]
                },
                {
                  icon: Shield,
                  title: "Conformité Native",
                  description: "Assurez la conformité réglementaire dès la conception.",
                  features: [
                    "Standards du secteur",
                    "Audit trail complet",
                    "Sécurité by design",
                    "Mise à jour régulière"
                  ]
                },
                {
                  icon: Rocket,
                  title: "Time-to-Market Optimal",
                  description: "Réduisez significativement vos délais de mise sur le marché.",
                  features: [
                    "Développement agile",
                    "Déploiement continu",
                    "Tests automatisés",
                    "Monitoring intégré"
                  ]
                }
              ].map((benefit, index) => (
                <div
                  key={index}
                  data-animate
                  id={`benefit-${index}`}
                  style={{
                    opacity: isVisible[`benefit-${index}`] ? 1 : 0,
                    transform: isVisible[`benefit-${index}`] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: `all 1000ms ${index * 200}ms`
                  }}
                >
                  <BenefitCard {...benefit} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technical Expertise Section */}
      <section className="py-16 md:py-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6"
                  data-animate
                  id="expertise-title"
                  style={{
                    opacity: isVisible['expertise-title'] ? 1 : 0,
                    transform: isVisible['expertise-title'] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: 'all 1000ms'
                  }}>
                Expertise Technique
              </h2>
              <p className="text-lg md:text-xl text-white/70">
                Des technologies modernes pour des solutions performantes
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                {
                  icon: GitBranch,
                  title: "Cloud Native",
                  description: "Architecture microservices sur cloud"
                },
                {
                  icon: Database,
                  title: "Big Data",
                  description: "Traitement données massives"
                },
                {
                  icon: Shield,
                  title: "Cybersécurité",
                  description: "Protection données avancée"
                },
                {
                  icon: Cpu,
                  title: "IA & ML",
                  description: "Intelligence artificielle"
                }
              ].map((expertise, index) => (
                <div
                  key={index}
                  data-animate
                  id={`expertise-${index}`}
                  style={{
                    opacity: isVisible[`expertise-${index}`] ? 1 : 0,
                    transform: isVisible[`expertise-${index}`] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: `all 1000ms ${index * 200}ms`
                  }}
                >
                  <ExpertiseCard {...expertise} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-transparent to-black/30 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6"
                  data-animate
                  id="process-title"
                  style={{
                    opacity: isVisible['process-title'] ? 1 : 0,
                    transform: isVisible['process-title'] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: 'all 1000ms'
                  }}>
                Notre Méthodologie
              </h2>
              <p className="text-lg md:text-xl text-white/70">
                Une approche structurée en 4 étapes pour des projets maîtrisés
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:gap-8 max-w-3xl mx-auto">
              {[
                {
                  number: "01",
                  title: "Co-Design",
                  description: "Phase de conception collaborative avec vos experts métier.",
                  features: [
                    "Analyse des besoins",
                    "Design thinking",
                    "Proof of Concept",
                    "Architecture solution",
                    "Planning projet"
                  ]
                },
                {
                  number: "02",
                  title: "Développement",
                  description: "Phase de développement agile et itérative.",
                  features: [
                    "Sprints agiles",
                    "Clean code",
                    "Code review",
                    "Tests unitaires",
                    "Intégration continue"
                  ]
                },
                {
                  number: "03",
                  title: "Validation",
                  description: "Phase complète de tests et certifications.",
                  features: [
                    "Tests fonctionnels",
                    "Tests performance",
                    "Tests sécurité",
                    "Tests utilisateurs",
                    "Certification solution"
                  ]
                },
                {
                  number: "04",
                  title: "Déploiement",
                  description: "Mise en production et support post-déploiement.",
                  features: [
                    "Déploiement continu",
                    "Migration données",
                    "Formation équipes",
                    "Support production",
                    "Maintenance évolutive"
                  ]
                }
              ].map((step, index) => (
                <div
                  key={index}
                  data-animate
                  id={`process-${index}`}
                  style={{
                    opacity: isVisible[`process-${index}`] ? 1 : 0,
                    transform: isVisible[`process-${index}`] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: `all 1000ms ${index * 200}ms`
                  }}
                >
                  <ProcessStep {...step} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
              <div
                data-animate
                id="services-section"
                style={{
                  opacity: isVisible['services-section'] ? 1 : 0,
                  transform: isVisible['services-section'] ? 'translateX(0)' : 'translateX(-2rem)',
                  transition: 'all 1000ms'
                }}
              >
                <h2 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8">Services Inclus</h2>
                <div className="space-y-6">
                  {[
                    {
                      icon: HeartHandshake,
                      title: "Conseil Architecture",
                      description: "Expertise en architecture des solutions financières."
                    },
                    {
                      icon: Wrench,
                      title: "DevOps Management",
                      description: "Pipeline CI/CD et infrastructure as code."
                    },
                    {
                      icon: BookOpen,
                      title: "Formation",
                      description: "Formation technique et transfert de compétences."
                    },
                    {
                      icon: Settings,
                      title: "Support & Maintenance",
                      description: "Support technique et maintenance évolutive."
                    }
                  ].map((service, index) => (
                    <ServiceItem key={index} {...service} />
                  ))}
                </div>
              </div>
              
              <div
                data-animate
                id="guarantees-section"
                style={{
                  opacity: isVisible['guarantees-section'] ? 1 : 0,
                  transform: isVisible['guarantees-section'] ? 'translateX(0)' : 'translateX(2rem)',
                  transition: 'all 1000ms 200ms'
                }}
              >
                <h2 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8 mt-8 lg:mt-0">Garanties</h2>
                <div className="space-y-6">
                  {[
                    {
                      icon: Shield,
                      title: "Qualité Code",
                      description: "Standards de qualité et clean code garantis."
                    },
                    {
                      icon: Clock,
                      title: "Respect Délais",
                      description: "Planning et milestone garantis."
                    },
                    {
                      icon: CheckCircle,
                      title: "Conformité",
                      description: "Respect des normes et standards du secteur."
                    },
                    {
                      icon: BarChart,
                      title: "Performance",
                      description: "Optimisation et scalabilité garanties."
                    },
                  ].map((guarantee, index) => (
                    <ServiceItem key={index} {...guarantee} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-transparent to-black/30 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center"
               data-animate
               id="cta-section"
               style={{
                 opacity: isVisible['cta-section'] ? 1 : 0,
                 transform: isVisible['cta-section'] ? 'translateY(0)' : 'translateY(2rem)',
                 transition: 'all 1000ms'
               }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6">
              Prêt à Développer Votre Solution ?
            </h2>
            <p className="text-lg md:text-xl text-white/70 mb-8 md:mb-12">
              Nos experts sont à votre disposition pour étudier votre projet
              et vous proposer la meilleure approche de développement.
            </p>
            
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="group px-6 md:px-8 py-3 md:py-4 rounded-lg bg-gradient-to-r from-green-500 
                        to-[#017D7E] hover:from-green-600 hover:to-emerald-600 text-white font-medium 
                        transition-all flex items-center justify-center gap-2"
              >
                <span className="flex items-center gap-2">
                  Démarrer un Projet
                  <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              
              <button
                onClick={() => navigate('/portfolio')}
                className="group px-6 md:px-8 py-3 md:py-4 rounded-lg border border-green-500/50 
                        hover:bg-[#017D7E]/10 text-white font-medium transition-all relative 
                        overflow-hidden"
              >
                <span className="relative z-10">Voir nos Réalisations</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 
                            to-transparent -translate-x-full group-hover:translate-x-full 
                            transition-transform duration-1000" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Factory;